import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../../Components/Header";
import Footer from "../../../Components/Footer/";
import CustomModal from "../../../Components/CustomModal/";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../Features/Session/SessionSlice";
import "./Login.css";
import crypto from "crypto-browserify";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { useModalContext } from "../../../Context/ModalContext";
import raw from "../../../Utils/publicKey.pem";
import {
  authenticateUser,
  getLoginVerification,
  getProfile,
  getAdditionalFields,
  requestDeviceVerificationOTP,
  resetUserState,
} from "../../../Features/User/UserSlice";

const initialTime = 1 * 60;

function Login() {
  const labels = useSelector((state) => state.labels.data);
  const userState = useSelector((state) => state.user);
  const { isLoading, isAuthenticated, additionalFields } = userState;

  const [pemContents, setPemContents] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const { setAdditionalInformation } = useModalContext();

  const [otpButtonActive, setOtpButtonActive] = useState(true);

  React.useEffect(() => {
    if (token) {
      navigate("/");
    }
  }, [token, navigate]);

  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const [isChecked, setIsChecked] = useState(false);
  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [hashedPassword, setHashedPassword] = useState("");
  const [message, setMessage] = useState();
  const [tempAuthToken, setTempAuthToken] = useState();
  const [smsCode, setSmsCode] = useState("");
  const [refreshCondition, setRefreshCondition] = useState(0);
  const [isLoginFormVisible, setIsLoginFormVisible] = useState(true);
  const [isLoginVerificationFormVisible, setIsLoginVerificationFormVisible] =
    useState(false);
  const [verificationType, setVerificationType] = useState();
  const [timeLeft, setTimeLeft] = useState(initialTime);

  const [isDeviceVerificationRequired, setIsDeviceVerificationRequired] =
    useState(false);
  const [deviceOtp, setDeviceOtp] = useState("");

  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("deviceId", deviceId);
  }

  const handleInputSmsChange = (e) => {
    const numericInput = e.target.value.replace(/\D/g, "");
    setSmsCode(numericInput);
  };

  useEffect(() => {
    if (refreshCondition == 1) {
      if (timeLeft === 0) {
        setRefreshCondition(0);
        setOtpButtonActive(true);
        setTimeLeft(initialTime);
        if (!isDeviceVerificationRequired) {
          checkLogin();
        }
      } else {
        const intervalId = setInterval(() => {
          setTimeLeft(timeLeft - 1);
        }, 1000);
        return () => clearInterval(intervalId);
      }
    }
  }, [refreshCondition, timeLeft]);

  useEffect(() => {
    if (isLoginVerificationFormVisible) {
      if (timeLeft === 0) {
        setRefreshCondition(1);
      } else {
        const intervalId = setInterval(() => {
          setTimeLeft(timeLeft - 1);
        }, 1000);
        return () => clearInterval(intervalId);
      }
    }
  }, [timeLeft, isLoginVerificationFormVisible]);

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const [validationError, setValidationError] = useState({
    username: "",
    password: "",
  });

  const validateEmailFormat = (input) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidChars = /[<>{}\[\]]/;
    return emailRegex.test(input) && !invalidChars.test(input);
  };

  const handleUsernameChange = (e) => {
    let value = e.target.value;
    const sanitizedValue = value.replace(/[<>{}\[\]'"\\;()]/g, "");

    setUsername(sanitizedValue);

    if (!validateEmailFormat(sanitizedValue)) {
      setValidationError((prev) => ({
        ...prev,
        username: labels?.forms?.validations?.general,
      }));
    } else {
      setValidationError((prev) => ({ ...prev, username: "" }));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (!value) {
      setValidationError((prev) => ({
        ...prev,
        password: labels?.forms?.validations?.required,
      }));
    } else {
      setValidationError((prev) => ({ ...prev, password: "" }));
    }
  };

  // Handle authentication response
  useEffect(() => {
    const checkAdditionalFields = async () => {
      if (isAuthenticated && userState.userProfile) {
        // Check for additional fields after successful authentication
        const response = await dispatch(getAdditionalFields());
        const fields = response?.payload?.data;

        if (fields && fields.length > 0) {
          setAdditionalInformation(fields);
          navigate("/additional-information-required", { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      }
    };

    checkAdditionalFields();
  }, [
    isAuthenticated,
    userState.userProfile,
    dispatch,
    navigate,
    setAdditionalInformation,
  ]);

  const checkLogin = async () => {
    setValidationError({ username: "", password: "" });
    let hasError = false;

    if (!username) {
      setValidationError((prev) => ({
        ...prev,
        username: labels?.forms?.validations?.required,
      }));
      hasError = true;
    } else if (!validateEmailFormat(username)) {
      setValidationError((prev) => ({
        ...prev,
        username: labels?.forms?.validations?.general,
      }));
      hasError = true;
    }

    if (!password) {
      setValidationError((prev) => ({
        ...prev,
        password: labels?.forms?.validations?.required,
      }));
      hasError = true;
    }

    if (hasError) return;

    try {
      const result = await dispatch(
        authenticateUser({
          dvc_id: deviceId,
          email: username,
          password: hashedPassword,
          mobile_session: true,
        })
      );

      const response = result?.payload;

      if (result.type.includes("fulfilled") && response?.status === "Success") {
        setTempAuthToken(response?.data?.tempAuthToken || "");
        setVerificationType(response?.data?.twoFAMehtod || "Email");
        setIsDeviceVerificationRequired(
          response?.data?.deviceVerificationRequired
        );
        setIsLoginFormVisible(false);
        setIsLoginVerificationFormVisible(true);
      } else {
        // Handle error with labels
        let errorMessage = "";
        const errorKey = response?.message || result?.error?.message;

        if (errorKey === "API_Inavalid_IP") {
          errorMessage = labels?.messages?.API_Inavalid_IP;
        } else {
          errorMessage = labels?.messages?.SignIn_Authentication_Failed;
        }

        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } catch (error) {}
  };

  const checkLoginVerification = async () => {
    const requestBody = {
      grant_type: "password",
      username: tempAuthToken,
      password: smsCode,
      dvc_otp: deviceOtp || "",
      dvc_id: deviceId,
    };

    var formBody = [];
    for (var property in requestBody) {
      var encodedKey = encodeURIComponent(property);
      var encodedValue = encodeURIComponent(requestBody[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    formBody = formBody.join("&");

    try {
      const result = await dispatch(getLoginVerification(formBody));
      const response = result?.payload;

      if (result.type.includes("fulfilled") && response?.access_token) {
        localStorage.setItem("token", response?.access_token);

        // Get user profile - this will update Redux state
        const profileResult = await dispatch(getProfile());
        const profileData = profileResult?.payload?.data;

        if (profileData) {
          // Dispatch session login with token and profile
          dispatch(
            login({
              userToken: response.access_token,
              userProfile: JSON.stringify(profileData),
            })
          );
          // Navigation will be handled by the useEffect watching isAuthenticated
        }
      } else if (result.type.includes("rejected")) {
        // Handle error with labels - the error structure from /token/v2
        const errorResponse = result?.payload?.response?.data;
        const errorKey =
          errorResponse?.error || errorResponse?.error_description;

        const getDynamicMessage = (key) => {
          return labels?.messages?.[key] || key;
        };

        const dynamicMessage = getDynamicMessage(errorKey);

        toast.error(dynamicMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } catch (error) {
      toast.error(
        labels?.messages?.SignIn_Authentication_Failed || "Verification failed",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  useEffect(() => {
    async function getPubKey() {
      try {
        const response = await fetch(raw);
        const text = await response.text();
        setPemContents(text);
      } catch (error) {}
    }
    getPubKey();
  }, []);

  useEffect(() => {
    const pubKeyPem = pemContents;
    try {
      const buffer = Buffer.from(password, "utf16le");
      const encrypted = crypto.publicEncrypt(pubKeyPem, buffer);
      const encryptedBase64 = encrypted.toString("base64");
      setHashedPassword(encryptedBase64);
    } catch (error) {}
  }, [password, pemContents]);

  const requestDeviceOtp = async (event) => {
    event.preventDefault();
    setTimeLeft(initialTime);

    try {
      const result = await dispatch(
        requestDeviceVerificationOTP({
          dvc_id: deviceId,
          email: username,
        })
      );

      const response = result?.payload;

      if (result.type.includes("fulfilled") && response?.status === "Success") {
        setOtpButtonActive(false);
        const messageKey = response?.message;
        const getDynamicMessage = (key) => {
          return labels?.messages?.[key] || key;
        };
        const dynamicMessage = getDynamicMessage(messageKey);
        toast.success(dynamicMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } catch (error) {}
  };

  const [isDeviceOtpValid, setIsDeviceOtpValid] = useState(false);
  const [hasDeviceOtpBlurred, setHasDeviceOtpBlurred] = useState(false);

  const [isSmsCodeValid, setIsSmsCodeValid] = useState(false);
  const [hasSmsCodeBlurred, setHasSmsCodeBlurred] = useState(false);

  useEffect(() => {
    setIsDeviceOtpValid(deviceOtp?.length >= 6);
  }, [deviceOtp]);

  useEffect(() => {
    setIsSmsCodeValid(smsCode?.length >= 6);
  }, [smsCode]);

  useEffect(() => {
    if (isDeviceVerificationRequired === false && smsCode?.length === 6) {
      checkLoginVerification();
    }
  }, [smsCode]);

  useEffect(() => {
    if (deviceOtp?.length === 6 && smsCode?.length === 6) {
      checkLoginVerification();
    }
  }, [deviceOtp, smsCode]);

  return (
    <>
      <Header />

      <CustomModal
        show={show}
        handleClose={handleClose}
        title="Login"
        message={message}
      />

      <section className="login">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div className="text">
                <ul>
                  <li>
                    <h5>
                      <img src="images/icons/check-circle.svg" alt="" />
                      {labels?.auth?.title1}
                    </h5>
                    <p>{labels?.auth?.desc1}</p>
                  </li>
                  <li>
                    <h5>
                      <img src="images/icons/check-circle.svg" alt="" />
                      {labels?.auth?.title2}
                    </h5>
                    <p>{labels?.auth?.desc1}</p>
                  </li>
                  <li>
                    <h5>
                      <img src="images/icons/check-circle.svg" alt="" />
                      {labels?.auth?.title3}
                    </h5>
                    <p>{labels?.auth?.desc3}</p>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-5">
              {isLoginFormVisible && !localStorage.getItem("token") ? (
                <form>
                  <h4>{labels?.forms?.login?.title}</h4>
                  <div className="input-container">
                    <label htmlFor="email">{labels?.form?.email}</label>
                    <input
                      type="text"
                      placeholder={labels?.forms?.login?.email?.placeholder}
                      onChange={handleUsernameChange}
                      value={username}
                      className={`form-control ${
                        validationError.username ? "is-invalid" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {validationError.username && (
                      <div className="invalid-feedback">
                        {validationError.username}
                      </div>
                    )}
                  </div>
                  <div className="input-container">
                    <label htmlFor="password">{labels?.form?.password}</label>
                    <input
                      type="password"
                      placeholder={labels?.forms?.login?.password?.placeholder}
                      onChange={handlePasswordChange}
                      className={`form-control ${
                        validationError.password ? "is-invalid" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {validationError.password && (
                      <div className="invalid-feedback">
                        {validationError.password}
                      </div>
                    )}
                  </div>
                  <div className="checkbox">
                    <input
                      type="checkbox"
                      id="check"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      disabled={isLoading}
                    />
                  </div>
                  <input
                    type="button"
                    value={
                      isLoading ? "Loading..." : labels?.forms?.login?.button
                    }
                    className="button-style-01"
                    onClick={checkLogin}
                    disabled={isLoading}
                  />

                  <a href="/register" className="link">
                    {labels?.forms?.signUp?.title}
                  </a>
                  <a href="/forget-password" className="link">
                    {labels?.buttons?.forgotPassword}
                  </a>
                </form>
              ) : null}

              {isLoginVerificationFormVisible ? (
                <form>
                  <h4>{labels?.forms?.login?.title}</h4>
                  <div className="input-container">
                    {verificationType !== "Email" ? (
                      <label htmlFor="password">
                        {labels?.forms?.twoFaLogin?.googleAuth?.placeholder
                          ?.replace(
                            "$t(multiFactorAuth.length)",
                            labels?.multiFactorAuth?.length
                          )
                          .replace(
                            "$t(multiFactorAuth.name)",
                            labels?.multiFactorAuth?.Google
                          )}
                      </label>
                    ) : (
                      <label htmlFor="password">
                        {labels?.forms?.twoFaLogin?.emailAuth?.placeholder}
                      </label>
                    )}

                    <input
                      type="password"
                      placeholder={labels?.forms?.login?.password?.placeholder}
                      value={smsCode}
                      onChange={handleInputSmsChange}
                      onBlur={() => setHasSmsCodeBlurred(true)}
                      disabled={isLoading}
                    />

                    {!isSmsCodeValid && hasSmsCodeBlurred && (
                      <div className="invalid-feedback d-block mt-0">
                        {smsCode === null ||
                        smsCode === undefined ||
                        smsCode === "" ? (
                          <>{labels?.forms?.validations?.required}</>
                        ) : (
                          <>
                            {labels?.forms?.validations?.min?.replace(
                              "{{length}}",
                              labels?.multiFactorAuth?.length
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {isDeviceVerificationRequired === true && (
                    <>
                      <div className="form-group device-otp-form-group flex-column flex-lg-row flex-md-row">
                        <div className="input-field">
                          <label htmlFor="password" className="d-none">
                            {labels?.forms?.twoFaLogin?.deviceAuth?.placeholder}
                          </label>
                          <input
                            type="text"
                            placeholder={
                              labels?.forms?.twoFaLogin?.deviceAuth?.placeholder
                            }
                            onChange={(e) => setDeviceOtp(e.target.value)}
                            onBlur={() => setHasDeviceOtpBlurred(true)}
                            className="form-control"
                            value={deviceOtp}
                            autoComplete="off"
                            disabled={isLoading}
                          />
                        </div>
                        <div className="button-field">
                          <button
                            className="btn btn-primary"
                            disabled={otpButtonActive === false || isLoading}
                            onClick={requestDeviceOtp}
                          >
                            {otpButtonActive === false
                              ? labels?.forms?.twoFaLogin?.timeRemaining?.replace(
                                  "{{timeRemaining}}",
                                  formatTime()
                                )
                              : labels?.forms?.twoFaLogin?.requestDeviceOtp}
                          </button>
                        </div>
                      </div>
                      {!isDeviceOtpValid && hasDeviceOtpBlurred && (
                        <div className="invalid-feedback d-block mt-0">
                          {deviceOtp === null ||
                          deviceOtp === undefined ||
                          deviceOtp === "" ? (
                            <>{labels?.forms?.validations?.required}</>
                          ) : (
                            <>
                              {labels?.forms?.validations?.min?.replace(
                                "{{length}}",
                                labels?.multiFactorAuth?.length
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </form>
              ) : null}

              {localStorage.getItem("token") ? (
                <h5 style={{ color: "#ddd" }} align="center">
                  {labels?.generics?.alreadyLoggedIn}
                </h5>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Login;
